import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CalendarDaysIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { memberProfileAPI, buildCalendarFeedUrl, CalendarFeedResponse } from '../../services/api';

const maskUrl = (url: string): string => {
  // Show origin + ".../ical/" then mask the token, keeping the .ics suffix.
  const m = url.match(/^(.*\/ical\/)([^./]+)(\.ics)$/);
  if (!m) return url;
  return `${m[1]}••••••••••••${m[3]}`;
};

const CalendarSyncCard: React.FC = () => {
  const [feed, setFeed] = useState<CalendarFeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await memberProfileAPI.getCalendarFeed();
      setFeed(data);
    } catch {
      setFeed(null);
      setLoadError("Couldn't load calendar sync settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Clear pending copy-timer on unmount so we don't setState on a dead component.
  useEffect(() => () => {
    if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
  }, []);

  const url = feed && feed.feed_path ? buildCalendarFeedUrl(feed.feed_path) : '';

  const handleCopy = async () => {
    if (!url) return;
    setCopyError(null);
    try {
      // Clipboard API is undefined on non-secure (HTTP) origins and in some webviews.
      if (!navigator.clipboard?.writeText) {
        throw new Error('clipboard-unavailable');
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
        copyTimerRef.current = null;
      }, 2000);
    } catch {
      setCopyError("Couldn't copy automatically — select the URL above and copy it manually.");
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setRegenError(null);
    try {
      const next = await memberProfileAPI.regenerateCalendarFeed();
      setFeed(next);
      setRevealed(false);
      setConfirming(false);
    } catch {
      setRegenError("Couldn't regenerate the link. Try again in a moment.");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return null;

  // Load failure: show inline error + retry, no functional UI.
  if (loadError) {
    return (
      <Card className="p-6">
        <CardHeader title="Calendar Sync" />
        <div className="flex items-start gap-2 text-sm text-clinic-accent">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{loadError}</span>
        </div>
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={fetchFeed}>Retry</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <CardHeader title="Calendar Sync" />

      {feed && !feed.enabled && (
        <p className="text-sm text-clinic-muted">
          Calendar sync is coming soon. You'll be able to subscribe your phone or computer calendar
          to your Clinic Studios bookings and breaks.
        </p>
      )}

      {feed && feed.enabled && (
        <>
          <p className="text-sm text-clinic-muted mb-3">
            Subscribe your calendar to see your bookings and breaks. This link is private — anyone with
            it can view your schedule, so don't share it.
          </p>

          <div className="bg-clinic-cream rounded-lg p-3 mb-3">
            <div className="flex items-center">
              <CalendarDaysIcon className="h-4 w-4 text-clinic-muted mr-2 flex-shrink-0" />
              <p className="text-sm text-clinic-dark break-all font-mono">
                {revealed ? url : maskUrl(url)}
              </p>
            </div>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              className="flex-1"
              leftIcon={copied ? <CheckCircleIcon className="h-4 w-4 text-clinic-primary" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
            >
              {copied ? 'Copied!' : 'Copy URL'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRevealed(r => !r)}
              title={revealed ? 'Hide URL' : 'Reveal URL'}
            >
              {revealed ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setRegenError(null); setConfirming(true); }}
              title="Regenerate link"
            >
              <ArrowPathIcon className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {copyError && (
            <p className="mt-2 text-xs text-clinic-accent">{copyError}</p>
          )}

          {confirming && (
            <div className="mt-3 p-3 bg-clinic-accent/10 border border-clinic-accent/30 rounded-lg">
              <p className="text-sm text-clinic-dark mb-2">
                Regenerating will <strong>break any calendar already subscribed</strong> to the old link.
                You'll need to re-subscribe with the new URL. Continue?
              </p>
              {regenError && (
                <p className="text-xs text-clinic-accent mb-2">{regenError}</p>
              )}
              <div className="flex space-x-2">
                <Button variant="primary" size="sm" onClick={handleRegenerate} isLoading={regenerating}>
                  Regenerate
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={regenerating}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <details className="mt-3">
            <summary className="text-xs text-clinic-primary cursor-pointer hover:underline">
              How to subscribe
            </summary>
            <div className="text-xs text-clinic-muted mt-2 space-y-1">
              <p><strong>Google Calendar:</strong> Other calendars → + → From URL → paste the link.</p>
              <p><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste the link.</p>
              <p><strong>Outlook:</strong> Add calendar → Subscribe from web → paste the link.</p>
              <p className="italic">Calendars refresh on their own schedule (often hourly), not instantly.</p>
            </div>
          </details>
        </>
      )}
    </Card>
  );
};

export default CalendarSyncCard;
